'use client'

import { useEffect, useState } from 'react'
import Script from 'next/script'

interface MarketingCodes {
  googleTagManager: string
  googleAnalytics4: string
  googleSearchConsole: string
  bingWebmasterTools: string
  facebookPixel: string
  googleAdsTag: string
  hotjar: string
  kommoChat: string
}

export default function MarketingScripts() {
  const [codes, setCodes] = useState<MarketingCodes>({
    googleTagManager: '',
    googleAnalytics4: '',
    googleSearchConsole: '',
    bingWebmasterTools: '',
    facebookPixel: '',
    googleAdsTag: '',
    hotjar: '',
    kommoChat: ''
  })
  // Gate marketing-script load on first user interaction or browser idle.
  // This frees the main thread during initial render so LCP/TBT stay low.
  const [shouldLoad, setShouldLoad] = useState(false)

  // Initialize dataLayer immediately when component mounts
  useEffect(() => {
    // Ensure dataLayer exists before any GTM scripts load
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  }, [])

  // Trigger marketing load on first interaction OR idle (whichever comes first).
  // 4s fallback ensures scripts fire even for "read-only" sessions.
  useEffect(() => {
    if (typeof window === 'undefined') return
    let triggered = false
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'scroll', 'touchstart']
    const useIdle = typeof window.requestIdleCallback === 'function'
    let idleHandle = 0

    const cancelIdle = () => {
      if (!idleHandle) return
      if (useIdle) window.cancelIdleCallback(idleHandle)
      else window.clearTimeout(idleHandle)
      idleHandle = 0
    }

    const trigger = () => {
      if (triggered) return
      triggered = true
      setShouldLoad(true)
      events.forEach(e => window.removeEventListener(e, trigger))
      cancelIdle()
    }

    events.forEach(e => window.addEventListener(e, trigger, { passive: true, once: true }))

    idleHandle = useIdle
      ? window.requestIdleCallback(trigger, { timeout: 4000 })
      : window.setTimeout(trigger, 4000) as unknown as number

    return () => {
      events.forEach(e => window.removeEventListener(e, trigger))
      cancelIdle()
    }
  }, [])

  useEffect(() => {
    if (!shouldLoad) return
    // Load marketing codes from API
    const loadCodes = async () => {
      try {
        const response = await fetch('/api/marketing/codes')
        if (response.ok) {
          const data = await response.json()
          setCodes(data)
        }
      } catch {
        // Failed to load marketing codes
      }
    }

    loadCodes()
  }, [shouldLoad])

  // Load all marketing scripts dynamically to handle HTML content properly
  useEffect(() => {
    // Helper function to process marketing code with HTML content
    const processMarketingCode = (code: string, scriptPrefix: string) => {
      if (!code) return

      // Remove existing scripts with this prefix
      const existingScripts = document.querySelectorAll(`[id^="${scriptPrefix}"]`)
      existingScripts.forEach(script => script.remove())

      // Create a temporary div to parse HTML content
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = code
      
      // Extract and execute scripts from the HTML content
      const scripts = tempDiv.querySelectorAll('script')
      scripts.forEach((originalScript, index) => {
        const newScript = document.createElement('script')
        newScript.id = `${scriptPrefix}-${index}`
        
        // Copy attributes
        Array.from(originalScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value)
        })
        
        // Copy content
        if (originalScript.src) {
          newScript.src = originalScript.src
        } else {
          newScript.textContent = originalScript.textContent
        }
        
        document.head.appendChild(newScript)
      })

      // Handle noscript tags and other elements
      const noscripts = tempDiv.querySelectorAll('noscript')
      noscripts.forEach((noscript, index) => {
        const newNoscript = document.createElement('noscript')
        newNoscript.id = `${scriptPrefix}-noscript-${index}`
        newNoscript.innerHTML = noscript.innerHTML
        document.head.appendChild(newNoscript)
      })
    }

    // Process all marketing codes
    processMarketingCode(codes.googleTagManager, 'gtm-script')
    processMarketingCode(codes.googleAnalytics4, 'ga4-script')
    processMarketingCode(codes.googleSearchConsole, 'gsc-script')
    processMarketingCode(codes.bingWebmasterTools, 'bing-script')
    processMarketingCode(codes.facebookPixel, 'fb-pixel-script')
    processMarketingCode(codes.googleAdsTag, 'google-ads-script')
    processMarketingCode(codes.hotjar, 'hotjar-script')

    // Handle Kommo Chat with special positioning styles
    if (codes.kommoChat) {
      processMarketingCode(codes.kommoChat, 'kommo-chat-script')
      
      // Add CSS styles for Kommo chat positioning
      const addKommoChatStyles = () => {
        const existingStyles = document.getElementById('kommo-chat-styles')
        if (existingStyles) {
          existingStyles.remove()
        }
        
        const style = document.createElement('style')
        style.id = 'kommo-chat-styles'
        style.textContent = `
          /* Kommo Chat positioning styles */
          #kommo-chat-widget,
          [id*="kommo"],
          [class*="kommo"],
          [data-kommo],
          .kommo-chat-widget,
          .kommo-widget {
            position: fixed !important;
            bottom: 0 !important;
            right: 0 !important;
            z-index: 999999 !important;
            max-width: 350px !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }
          
          /* Kommo chat button positioning */
          #kommo-chat-button,
          [id*="kommo-button"],
          [class*="kommo-button"],
          .kommo-chat-button {
            position: fixed !important;
            bottom: 0 !important;
            right: 0 !important;
            z-index: 999999 !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }
          
          /* Remove any default margins/paddings from Kommo elements */
          #kommo-chat-widget *,
          [id*="kommo"] *,
          [class*="kommo"] *,
          [data-kommo] *,
          .kommo-chat-widget *,
          .kommo-widget * {
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            outline: none !important;
          }
          
          /* Mobile specific positioning */
          @media (max-width: 768px) {
            #kommo-chat-widget,
            [id*="kommo"],
            [class*="kommo"],
            [data-kommo],
            .kommo-chat-widget,
            .kommo-widget {
              position: fixed !important;
              bottom: 0 !important;
              right: 0 !important;
              left: auto !important;
              top: auto !important;
              transform: none !important;
              max-width: 280px !important;
              margin: 0 !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
            }
            
            #kommo-chat-button,
            [id*="kommo-button"],
            [class*="kommo-button"],
            .kommo-chat-button {
              position: fixed !important;
              bottom: 0 !important;
              right: 0 !important;
              left: auto !important;
              top: auto !important;
              transform: none !important;
              margin: 0 !important;
              padding: 0 !important;
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
            }
            
            /* Override any centering styles */
            [id*="kommo"] *,
            [class*="kommo"] *,
            [data-kommo] *,
            .kommo-widget * {
              left: auto !important;
              right: auto !important;
              transform: none !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
            }
          }
        `
        document.head.appendChild(style)
      }
      
      // Add styles immediately and also after a delay to ensure chat widget is loaded
      addKommoChatStyles()
      setTimeout(addKommoChatStyles, 1000)
      setTimeout(addKommoChatStyles, 3000)
    }
  }, [codes.googleTagManager, codes.googleAnalytics4, codes.googleSearchConsole, codes.bingWebmasterTools, codes.facebookPixel, codes.googleAdsTag, codes.hotjar, codes.kommoChat])

  return (
    <>
      {/* All marketing scripts are now loaded dynamically via useEffect to handle HTML content properly */}
    </>
  )
}